import {
  useMutation,
  UseMutationResult,
  useQueryClient,
} from '@tanstack/react-query'
import { useApiWithIDToken } from '../../state/modules/common'
import { useNotifications } from '../../state/modules/notifications'
import { Variant } from '../../common/types'
import { getUploadDisplayName } from '../../utils/getUploadDisplayName'
import { exceedsMaxUploadSize, maxDocumentUploadSizeMB } from '../../utils/file'
import { useProgressToast } from './helpers/useProgressToast'
import JSZip from 'jszip'
import axios from 'axios'

type UploadChecklistDocumentVariables = {
  documentId: number
  documentName: string
  files: File[]
}

/**
 * Upload file(s) for a given checklist document.
 * @returns A UseMutationResult object for triggering the upload.
 */
export const useUploadChecklistDocument = (): UseMutationResult<
  void,
  Error,
  UploadChecklistDocumentVariables
> => {
  const api = useApiWithIDToken()
  const queryClient = useQueryClient()
  const { send: sendNotification } = useNotifications()
  const { startProgress, endProgress } = useProgressToast()

  return useMutation<
    void,
    Error,
    UploadChecklistDocumentVariables,
    { toastId: string }
  >({
    mutationFn: ({ documentId, files }) =>
      uploadChecklistDocument(api, documentId, files),
    onMutate: (vars) => ({
      toastId: startProgress(
        'Uploading Document',
        `${vars.files.length} ${
          vars.files.length === 1 ? 'file' : 'files'
        } uploading to ${vars.documentName}`
      ),
    }),
    onSuccess: (_data, vars) => {
      void queryClient.invalidateQueries({
        queryKey: ['siteRFPChecklists'],
        refetchType: 'active',
      })
      void queryClient.invalidateQueries({
        queryKey: ['siteRFPDocumentSummary'],
        refetchType: 'active',
      })
      sendNotification({
        title: 'Upload Successful',
        message: `${getFileText(vars.files)} ${
          vars.files.length === 1 ? 'has' : 'have'
        } been successfully uploaded to ${vars.documentName}.`,
        useRucksackUI: true,
      })
    },
    onError: (error, vars) => {
      sendNotification({
        title: 'Upload Failed',
        message: isFileTooLargeError(error)
          ? `${getFileText(
              vars.files
            )} exceeds the maximum allowed size of ${maxDocumentUploadSizeMB} MB. Please reduce the file size and try again.`
          : isConflictError(error)
          ? 'This document is already being uploaded. Please refresh and try again.'
          : `An error occurred while trying to upload ${getFileText(
              vars.files
            )} to ${vars.documentName}. Please try again.`,
        variant: Variant.DANGER,
        useRucksackUI: true,
      })
    },
    onSettled: (_data, _error, _vars, context) => {
      endProgress(context?.toastId)
    },
  })
}

function getFileText(files: File[]): string {
  if (files.length === 1) {
    return `${files[0].name}`
  }
  return `${files.length} files`
}

/**
 * Whether a failed upload was rejected because the document is already being uploaded.
 * The server returns 409 Conflict when an upload for the same document is already in
 * progress, so we show specific guidance instead of the generic failure message.
 */
function isConflictError(error: unknown): boolean {
  return axios.isAxiosError(error) && error.response?.status === 409
}

class FileTooLargeError extends Error {}

/**
 * Whether a failed upload was rejected because the file(s) exceeded the maximum
 * allowed size, either by our own pre-flight check or by the server's 413 response.
 */
function isFileTooLargeError(error: unknown): boolean {
  return (
    error instanceof FileTooLargeError ||
    (axios.isAxiosError(error) && error.response?.status === 413)
  )
}

async function uploadChecklistDocument(
  api: ReturnType<typeof useApiWithIDToken>,
  documentId: number,
  files: File[]
): Promise<void> {
  if (exceedsMaxUploadSize(files)) {
    throw new FileTooLargeError(
      `File(s) exceed the maximum allowed size of ${maxDocumentUploadSizeMB} MB`
    )
  }

  // zip the file(s)
  const zip = new JSZip()
  for (const file of files) {
    zip.file(file.name, file)
  }
  const zipContent = await zip.generateAsync({ type: 'blob' })

  const url = `/documents/${documentId}`
  const data = new FormData()
  data.append('file', zipContent)
  data.append('name', getUploadDisplayName(files))
  const config = {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  }
  await api.put(url, data, config)
}
